// api/paging.mjs — SB-159（第三百八十二批，需求方实撞）：列表分页的客户端一半。纯函数（SB-042）。
//
// ── 病灶 ──────────────────────────────────────────────────────────────────────
// 审批浏览「状态=全部、类型=全部」的缺省视图静静停在最旧的 200 张：页面只发一次请求、
// 服务端缺省一页 200、按 id 升序、应答此前只有 {tickets}。最新的事故单（INC-000010/011，仍开着）
// 掉出那一页，而窄筛选装得进一页——于是它们**只在明确选「事故」时才看得见**。
// 「取全了」与「取了一半」在页面上长得一模一样，这是本仓最危险的那一族病（坑册：一次失败返回
// 了与合法状态完全相同的东西）。
//
// ── 这里的三件东西 ─────────────────────────────────────────────────────────────
//   · fetchAllPages —— 工作队列（按状态分组、要看全集的页面）一页一页取到底：只在服务端说
//     `hasMore === true` 时才接着取，沿 `after = nextCursor` 走；**任何一页失败整次失败**
//     （口径 3：绝不把半份清单当完整的呈现）；hasMore 为真却给不出前进的游标 ⇒ 拒绝
//     （PAGING_NO_CURSOR），而不是原地打转或悄悄停下；旧后端（hasMore 不是布尔）⇒ 只取第一页
//     并记 legacy，由 pagingNotice 说「可能不全」。
//   · pagingNotice —— 把一次取数的结局翻成页上的一句话（或 null）：撞了页数上限、旧后端满页、
//     取到的张数与服务端的 total 对不上，三种各说各的。**静默是唯一不许的结局**。键名的前缀由页面给
//     （opts.ns）：那几句话里有量词（张／条），也有「点重试就能补齐」这种只对某一页成立的承诺。
//   · hasMoreNote —— 浏览型视图（不取到底）的「还有更多」：只认严格的 `hasMore === true`。
//
// 依赖注入：fetchPage({ limit, after }) 由调用方给（页面里就是 api.approverTickets），于是判据
// 不必起服务器、也不必 mock fetch。

export const PAGING_NO_CURSOR = 'PAGING_NO_CURSOR';

// 带 code 的错误：页面的 useLoad 会把它当 error 态（与网络失败同一个出口——重试按钮在那里）。
export class PagingError extends Error {
  constructor(message, extra = {}) {
    super(message);
    this.name = 'PagingError';
    this.code = PAGING_NO_CURSOR;
    this.extra = extra;
  }
}

// 一页一页取到底。返回 { rows, total, pages, capped, legacy }：
//   rows   —— 按 keyOf 去重后的全部行（翻页途中有行插入或游标重叠，去重兜住）；
//   total  —— 服务端最后一页给的 total（没给 ⇒ null）；
//   pages  —— 实际取了几页；
//   capped —— 撞了 maxPages 而服务端仍说还有（页上要说「只列了前 N 张」）；
//   legacy —— 应答里 hasMore 不是布尔（旧后端）：只取了第一页，是否取全**未知**。
export async function fetchAllPages(fetchPage, {
  rowsKey, pageLimit, maxPages = 50, keyOf = (r) => r?.no,
} = {}) {
  const seen = new Set();
  const rows = [];
  let total = null;
  let after;
  for (let pages = 1; ; pages++) {
    // 不 catch：任何一页的失败都让整次加载失败（口径 3）。
    const body = await fetchPage({ limit: pageLimit, after });
    const got = Array.isArray(body?.[rowsKey]) ? body[rowsKey] : [];
    for (const r of got) {
      const k = keyOf(r);
      if (k !== undefined && k !== null) {
        if (seen.has(k)) continue;
        seen.add(k);
      }
      rows.push(r);
    }
    if (typeof body?.total === 'number') total = body.total;
    if (typeof body?.hasMore !== 'boolean') return { rows, total, pages, capped: false, legacy: true };
    if (body.hasMore !== true) return { rows, total, pages, capped: false, legacy: false };
    const next = body.nextCursor;
    if (next === undefined || next === null || next === after) {
      throw new PagingError(
        `server says hasMore but gave ${next === after ? 'the same' : 'no'} cursor (page ${pages})`,
        { pages, after, next });
    }
    if (pages >= maxPages) return { rows, total, pages, capped: true, legacy: false };
    after = next;
  }
}

// 一次取数的结局 → 页上那一句（{key, kind, params, severity}）或 null。
// 次序即优先级：撞上限 > 旧后端满页 > 张数与 total 对不上。三者都不是 ⇒ null（取全了）。
// kind 是结局名（capped／maybeMore／drift），页面按它决定挂不挂重试；key ＝ `${ns}.${kind}`。
// 复审（N7）：ns 必填——此前三句是通用键 paging.*，却写死了「张」，而且 drift 那句承诺「点重试重新取全」，
// 那句话只对审批页（它自己一页页取到底）成立；「我的工单」不分页，对不上时可能是服务端加了上限，
// 重试补不回来。现在审批页是 approvals.paging.*，我的工单是 tickets.paging.drift（它只会走到 drift）。
// 漏给 ns 是调用方的编程错误：直接抛，而不是给页面一个不存在的键（那会把裸键名印给用户）。
export function pagingNotice(result, { pageLimit, ns } = {}) {
  if (typeof ns !== 'string' || ns === '') {
    throw new TypeError('pagingNotice: opts.ns (the page\'s own i18n key prefix) is required');
  }
  if (!result) return null;
  const n = Array.isArray(result.rows) ? result.rows.length : 0;
  const total = typeof result.total === 'number' ? result.total : null;
  const say = (kind, params, severity) => ({ key: `${ns}.${kind}`, kind, params, severity });
  if (result.capped) return say('capped', { n, total: total ?? '?' }, 'warning');
  if (result.legacy && typeof pageLimit === 'number' && n >= pageLimit) return say('maybeMore', { n }, 'warning');
  // 服务端说 hasMore=false 而 total 比取到的多（或翻页途中世界变了）：**不许静默**。
  if (total !== null && total !== n) return say('drift', { n, total }, 'info');
  return null;
}

// 浏览型视图的「还有更多没列出来」：只认严格的 true——旧后端（缺键）与 false 一律不出声，
// 因为一句永远在那儿的「还有更多」等于没说话（SB-159 §5 第一百三十一批那条）。
export function hasMoreNote(body) {
  if (body?.hasMore !== true) return null;
  return { key: 'paging.hasMore', params: { n: body.limit ?? '?' } };
}
